import * as React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../runtimeConfig';

export type FeatureMap = Record<string, boolean>;

interface FeaturesContextValue {
  features: FeatureMap;
  loading: boolean;
  isEnabled: (service?: string) => boolean;
}

const FeaturesContext = createContext<FeaturesContextValue | undefined>(undefined);

export function FeaturesProvider({ children }: { children: React.ReactNode }) {
  const [features, setFeatures] = useState<FeatureMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api-service/features`)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((data) => {
        if (active && data && typeof data.features === 'object') {
          setFeatures(data.features as FeatureMap);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<FeaturesContextValue>(
    () => ({
      features,
      loading,
      isEnabled: (service?: string) => (service ? features[service] !== false : true),
    }),
    [features, loading]
  );

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}

export function useFeatures(): FeaturesContextValue {
  const context = useContext(FeaturesContext);
  if (!context) {
    return { features: {}, loading: false, isEnabled: () => true };
  }
  return context;
}
